import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Activity, AlertTriangle, Thermometer, Droplet, Zap, Gauge, RefreshCw, Filter, Database } from "lucide-react";
import { toast } from "react-toastify";
import {
  getIoTReadingsByAsset,
  getAllIoTReadings,
  getActiveIoTAlertsCount,
  getAllIoTAlerts,
  generateDummyIoTData,
} from "../../../service/api";
import { IoTReading, IoTAlert } from "../../../types/api";
import { getAssetInventory } from "../../../service/api";
import { usePermissions } from "../../../hooks/usePermission";
import { MONITORING_IOT_INTEGRATION } from "../../../constants/modules";

const IoTDashboard: React.FC = () => {
  const { canRead } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [readings, setReadings] = useState<IoTReading[]>([]);
  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string>("");
  const [selectedSensorType, setSelectedSensorType] = useState<string>("");
  const [timeRange, setTimeRange] = useState<number>(24); // hours
  const [alertStats, setAlertStats] = useState({ active: 0, critical: 0 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const sensorTypes = [
    { value: "energy", label: "Energy", icon: Zap, color: "#f59e0b" },
    { value: "water_flow", label: "Water Flow", icon: Droplet, color: "#3b82f6" },
    { value: "temperature", label: "Temperature", icon: Thermometer, color: "#ef4444" },
    { value: "humidity", label: "Humidity", icon: Gauge, color: "#10b981" },
    { value: "pressure", label: "Pressure", icon: Gauge, color: "#8b5cf6" },
  ];

  const fetchAssets = useCallback(async () => {
    try {
      const response = await getAssetInventory();
      if (response.data?.data) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      } else if (response.data?.results) {
        setAssets(Array.isArray(response.data.results) ? response.data.results : []);
      } else if (response.data?.message) {
        setAssets(Array.isArray(response.data.message) ? response.data.message : []);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  }, []);

  const fetchReadings = useCallback(async () => {
    if (!canRead(MONITORING_IOT_INTEGRATION)) return;

    try {
      setLoading(true);
      let response;

      if (selectedAsset) {
        response = await getIoTReadingsByAsset(selectedAsset, {
          hours: timeRange,
          sensorType: selectedSensorType || undefined,
        });
      } else {
        response = await getAllIoTReadings({
          limit: 1000,
          where: JSON.stringify({
            timestamp: {
              $gte: new Date(Date.now() - timeRange * 60 * 60 * 1000).toISOString(),
            },
            ...(selectedSensorType && { sensorType: selectedSensorType }),
          }),
        });
      }

      let fetchedReadings: IoTReading[] = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          fetchedReadings = response.data.data;
        } else if (response.data.data.results) {
          fetchedReadings = response.data.data.results;
        }
      } else if (response.data?.results) {
        fetchedReadings = response.data.results as unknown as IoTReading[];
      } else if (response.data?.message) {
        fetchedReadings = Array.isArray(response.data.message) ? response.data.message : [];
      }

      setReadings(fetchedReadings);
    } catch (error: any) {
      toast.error("Failed to fetch IoT readings");
      console.error("Error fetching readings:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedAsset, selectedSensorType, timeRange]);

  const fetchAlerts = useCallback(async () => {
    if (!canRead(MONITORING_IOT_INTEGRATION)) return;

    try {
      const [alertsResponse, statsResponse] = await Promise.all([
        getAllIoTAlerts({ status: "active", limit: 50 }),
        getActiveIoTAlertsCount(),
      ]);

      let fetchedAlerts: IoTAlert[] = [];
      if (alertsResponse.data?.data) {
        if (Array.isArray(alertsResponse.data.data)) {
          fetchedAlerts = alertsResponse.data.data;
        } else if (alertsResponse.data.data.results) {
          fetchedAlerts = alertsResponse.data.data.results;
        }
      } else if (alertsResponse.data?.results) {
        fetchedAlerts = alertsResponse.data.results as unknown as IoTAlert[];
      } else if (alertsResponse.data?.message) {
        fetchedAlerts = Array.isArray(alertsResponse.data.message) ? alertsResponse.data.message : [];
      }

      setAlerts(fetchedAlerts);

      if (statsResponse.data?.data) {
        setAlertStats({
          active: statsResponse.data.data.active || 0,
          critical: statsResponse.data.data.critical || 0,
        });
      }
    } catch (error: any) {
      console.error("Error fetching alerts:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (canRead(MONITORING_IOT_INTEGRATION)) {
      fetchReadings();
    }
  }, [selectedAsset, selectedSensorType, timeRange]);

  useEffect(() => {
    if (canRead(MONITORING_IOT_INTEGRATION)) {
      fetchAlerts();
    }
  }, []);

  useEffect(() => {
    if (autoRefresh && canRead(MONITORING_IOT_INTEGRATION)) {
      refreshIntervalRef.current = setInterval(() => {
        const fetchData = async () => {
          try {
            setLoading(true);
            let response;

            if (selectedAsset) {
              response = await getIoTReadingsByAsset(selectedAsset, {
                hours: timeRange,
                sensorType: selectedSensorType || undefined,
              });
            } else {
              response = await getAllIoTReadings({
                limit: 1000,
                where: JSON.stringify({
                  timestamp: {
                    $gte: new Date(Date.now() - timeRange * 60 * 60 * 1000).toISOString(),
                  },
                  ...(selectedSensorType && { sensorType: selectedSensorType }),
                }),
              });
            }

            let fetchedReadings: IoTReading[] = [];
            if (response.data?.data) {
              if (Array.isArray(response.data.data)) {
                fetchedReadings = response.data.data;
              } else if (response.data.data.results) {
                fetchedReadings = response.data.data.results;
              }
            } else if (response.data?.results) {
              fetchedReadings = response.data.results as unknown as IoTReading[];
            } else if (response.data?.message) {
              fetchedReadings = Array.isArray(response.data.message) ? response.data.message : [];
            }

            setReadings(fetchedReadings);

            const [alertsResponse, statsResponse] = await Promise.all([
              getAllIoTAlerts({ status: "active", limit: 50 }),
              getActiveIoTAlertsCount(),
            ]);

            let fetchedAlerts: IoTAlert[] = [];
            if (alertsResponse.data?.data) {
              if (Array.isArray(alertsResponse.data.data)) {
                fetchedAlerts = alertsResponse.data.data;
              } else if (alertsResponse.data.data.results) {
                fetchedAlerts = alertsResponse.data.data.results;
              }
            } else if (alertsResponse.data?.results) {
              fetchedAlerts = alertsResponse.data.results as unknown as IoTAlert[];
            } else if (alertsResponse.data?.message) {
              fetchedAlerts = Array.isArray(alertsResponse.data.message) ? alertsResponse.data.message : [];
            }

            setAlerts(fetchedAlerts);

            if (statsResponse.data?.data) {
              setAlertStats({
                active: statsResponse.data.data.active || 0,
                critical: statsResponse.data.data.critical || 0,
              });
            }
          } catch (error: any) {
            console.error("Error refreshing data:", error);
          } finally {
            setLoading(false);
          }
        };

        fetchData();
      }, 30000);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [autoRefresh, selectedAsset, selectedSensorType, timeRange, canRead]);

  const chartData = useMemo(() => {
    if (readings.length === 0) return [];

    const grouped: Record<string, Record<string, number>> = {};
    const timeMap: Record<string, number> = {};

    readings.forEach((reading) => {
      const date = new Date(reading.timestamp);
      const timeKey = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
      const sensorKey = `${reading.sensorType}_${reading.sensorId}`;

      if (!grouped[sensorKey]) {
        grouped[sensorKey] = {};
      }
      if (grouped[sensorKey][timeKey]) {
        grouped[sensorKey][timeKey] = (grouped[sensorKey][timeKey] + reading.value) / 2;
      } else {
        grouped[sensorKey][timeKey] = reading.value;
      }
      timeMap[timeKey] = date.getTime();
    });

    const timeKeys = Object.keys(timeMap).sort((a, b) => timeMap[a] - timeMap[b]);

    return timeKeys.map((timeKey) => {
      const dataPoint: any = { time: timeKey };
      Object.keys(grouped).forEach((sensorKey) => {
        dataPoint[sensorKey] = grouped[sensorKey][timeKey] || null;
      });
      return dataPoint;
    });
  }, [readings]);

  const uniqueSensors = useMemo(() => {
    const sensorMap = new Map<string, { type: string; id: string; name: string; color: string }>();
    readings.forEach((reading) => {
      const key = `${reading.sensorType}`;
    //   _${reading.sensorId}`;
      if (!sensorMap.has(key)) {
        const sensorTypeInfo = sensorTypes.find((st) => st.value === reading.sensorType);
        sensorMap.set(key, {
          type: reading.sensorType,
          id: reading.sensorId,
          name: reading.sensorName || reading.sensorId,
          color: sensorTypeInfo?.color || "#6b7280",
        });
      }
    });
    return Array.from(sensorMap.values());
  }, [readings]);

  const handleRefresh = () => {
    fetchReadings();
    fetchAlerts();
    toast.success("Data refreshed");
  };

  const handleGenerateDummyData = async () => {
    if (!canRead(MONITORING_IOT_INTEGRATION)) return;

    try {
      setLoading(true);
      const assetId = selectedAsset || (assets.length > 0 ? assets[0].id || assets[0]._id : undefined);
      await generateDummyIoTData({ assetId, count: 100 });
      toast.success("Dummy data generated successfully! Refreshing...");
      setTimeout(() => {
        fetchReadings();
        fetchAlerts();
      }, 2000);
    } catch (error: any) {
      toast.error("Failed to generate dummy data");
      console.error("Error generating dummy data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IoT Monitoring Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Real-time sensor data and alerts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleGenerateDummyData}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            title="Generate dummy sensor data for testing"
          >
            <Database size={16} />
            Generate Test Data
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              autoRefresh
                ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <RefreshCw className={`inline mr-2 ${autoRefresh ? "animate-spin" : ""}`} size={16} />
            Auto Refresh
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={loading ? "animate-spin" : ""} size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Alerts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{alertStats.active}</p>
            </div>
            <AlertTriangle className="text-red-500" size={32} />
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Critical Alerts</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{alertStats.critical}</p>
            </div>
            <AlertTriangle className="text-red-600" size={32} />
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Sensors</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{uniqueSensors.length}</p>
            </div>
            <Activity className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Readings</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{readings.length}</p>
            </div>
            <Activity className="text-green-500" size={32} />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Asset</label>
            <select
              value={selectedAsset}
              onChange={(e) => setSelectedAsset(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm min-w-[200px]"
            >
              <option value="">All Assets</option>
              {assets.map((asset) => (
                <option key={asset.id || asset._id} value={asset.id || asset._id}>
                  {asset.name || asset.assetName || asset.id || asset._id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Sensor Type</label>
            <select
              value={selectedSensorType}
              onChange={(e) => setSelectedSensorType(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm min-w-[150px]"
            >
              <option value="">All Types</option>
              {sensorTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="border rounded-lg px-3 py-2 text-sm min-w-[120px]"
            >
              <option value={1}>Last Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={24}>Last 24 Hours</option>
              <option value={48}>Last 48 Hours</option>
              <option value={168}>Last Week</option>
            </select>
          </div>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sensor Readings Over Time</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              {uniqueSensors.map((sensor, index) => (
                <Line
                  key={sensor.id}
                  type="monotone"
                  dataKey={`${sensor.type}_${sensor.id}`}
                  name={sensor.name}
                  stroke={sensor.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-12 shadow-sm text-center">
          <Activity className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No readings available for the selected filters</p>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Active Alerts</h2>
          </div>
          <div className="divide-y">
            {alerts.slice(0, 10).map((alert) => (
              <div
                key={alert.id || alert._id}
                className={`px-6 py-4 ${
                  alert.severity === "critical"
                    ? "bg-red-50"
                    : alert.severity === "high"
                    ? "bg-orange-50"
                    : "bg-yellow-50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle
                        className={
                          alert.severity === "critical"
                            ? "text-red-600"
                            : alert.severity === "high"
                            ? "text-orange-600"
                            : "text-yellow-600"
                        }
                        size={18}
                      />
                      <span className="font-medium text-gray-900">{alert.assetName || alert.assetId}</span>
                      <span className="text-sm text-gray-600">- {alert.sensorName || alert.sensorType}</span>
                    </div>
                    <p className="text-sm text-gray-700">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Value: {alert.value} {alert.unit} | Triggered: {new Date(alert.triggeredAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IoTDashboard;

